/**
 * Curated PII infoType catalog (KIR-3837)
 *
 * Single source of truth shared by the backend scanner (sensitivity + defaults)
 * and the frontend policy UI (the "Detected PII Types" table + picker). The full
 * ~150 Google DLP infoTypes are discovered at runtime via `infoTypes.list`
 * (see the DLP engine / `GET .../pii/info-types` endpoint); this catalog is only
 * the product-curated rows shown by default and the metadata Kirin overlays on
 * top of DLP's list.
 *
 * `dlpName` values must be valid Google DLP infoType names. Off-by-default rows
 * are never scanned until an admin enables them, so an unconfirmed name there is
 * inert; only the default-ON set below is scanned out of the box.
 */
import { z } from 'zod';
/**
 * infoTypes the scanner never alerts on — findings are dropped before counting
 * (KIR-3034: person names are explicitly NOT treated as PII). Kept here so the
 * picker can exclude them: offering a type that can never alert would only add
 * inline DLP latency (KIR-3837). Must stay in sync with the scanner's drop set.
 */
export const NON_PII_INFO_TYPES = new Set([
    'PERSON_NAME',
    'FIRST_NAME',
    'LAST_NAME',
    'MALE_NAME',
    'FEMALE_NAME',
]);
/**
 * Google's higher-latency infoType detectors (KIR-3837). These add delay to every
 * inline scan, so they are always off by default and adding one via the picker
 * shows a confirmation and is added turned OFF.
 */
export const HIGHER_LATENCY_INFO_TYPES = new Set([
    'PERSON_NAME',
    'FEMALE_NAME',
    'MALE_NAME',
    'FIRST_NAME',
    'LAST_NAME',
    'DATE_OF_BIRTH',
    'LOCATION',
    'STREET_ADDRESS',
    'ORGANIZATION_NAME',
]);
/**
 * Curated PII types shown in the policy table by default. Order here is the
 * display order. `defaultOn: true` rows form the out-of-the-box scanned set.
 * Regional / country-specific types (national IDs, per-country bank/passport
 * variants, etc.) are intentionally not listed here — they are discovered
 * through the DLP picker's search (KIR-3837).
 */
export const CURATED_PII_INFO_TYPES = [
    {
        dlpName: 'EMAIL_ADDRESS',
        label: 'Email Address',
        sensitivity: 'not-sensitive',
        higherLatency: false,
        defaultOn: true,
        example: 'jane.doe@example.com',
    },
    {
        dlpName: 'PHONE_NUMBER',
        label: 'Phone Number',
        sensitivity: 'not-sensitive',
        higherLatency: false,
        defaultOn: true,
        example: '+44 20 7946 0958',
    },
    {
        dlpName: 'US_SOCIAL_SECURITY_NUMBER',
        label: 'US Social Security Number',
        sensitivity: 'sensitive',
        higherLatency: false,
        defaultOn: true,
        example: '078-05-1120',
    },
    {
        dlpName: 'CREDIT_CARD_NUMBER',
        label: 'Credit Card Number',
        sensitivity: 'sensitive',
        higherLatency: false,
        defaultOn: true,
        example: '4111 1111 1111 1111',
    },
    {
        dlpName: 'DATE_OF_BIRTH',
        label: 'Date of Birth',
        sensitivity: 'not-sensitive',
        higherLatency: true,
        defaultOn: false,
        example: 'DOB: 1990-07-14',
    },
    {
        dlpName: 'IBAN_CODE',
        label: 'IBAN',
        sensitivity: 'sensitive',
        higherLatency: false,
        defaultOn: true,
        example: 'DE89 3704 0044 0532 0130 00',
    },
    {
        dlpName: 'PASSPORT',
        label: 'Passport',
        sensitivity: 'sensitive',
        higherLatency: false,
        defaultOn: true,
        example: 'Passport No: 340020013',
    },
    {
        dlpName: 'US_DRIVERS_LICENSE_NUMBER',
        label: "Driver's License Number",
        sensitivity: 'sensitive',
        higherLatency: false,
        defaultOn: false,
        example: "Driver's license: I1234568",
    },
    {
        dlpName: 'US_INDIVIDUAL_TAXPAYER_IDENTIFICATION_NUMBER',
        label: 'Tax ID (e.g. US ITIN)',
        sensitivity: 'sensitive',
        higherLatency: false,
        defaultOn: false,
        example: 'ITIN: 912-78-5678',
    },
    {
        dlpName: 'IP_ADDRESS',
        label: 'IP Address',
        sensitivity: 'not-sensitive',
        higherLatency: false,
        defaultOn: false,
        example: '192.168.1.1',
    },
    {
        dlpName: 'MEDICAL_RECORD_NUMBER',
        label: 'Medical Record Number',
        sensitivity: 'sensitive',
        higherLatency: false,
        defaultOn: false,
        example: 'MRN 00123456',
    },
];
/** DLP names scanned out of the box (defaultOn curated types). */
export const DEFAULT_PII_INFO_TYPES = CURATED_PII_INFO_TYPES.filter(t => t.defaultOn).map(t => t.dlpName);
const CURATED_BY_NAME = new Map(CURATED_PII_INFO_TYPES.map(t => [t.dlpName, t]));
/** Catalog lookup by DLP infoType name, or undefined for non-curated types. */
export function getCuratedPiiInfoType(dlpName) {
    return CURATED_BY_NAME.get(dlpName);
}
/**
 * Fallback sensitivity pattern for non-curated DLP types (national IDs,
 * passports, driver's licenses, tax IDs discovered via the picker). Kept in
 * sync with the backend classifier so a picker-added regional ID still tiers as
 * sensitive even though it is not in the curated catalog.
 */
export const SENSITIVE_INFO_TYPE_PATTERN = /NATIONAL_ID|NATIONAL_INSURANCE|PASSPORT|DRIVERS?_LICENSE|TAX|_NIR|_NINO|IDENTITY_CARD|_SSN|SOCIAL_SECURITY|BANK_ACCOUNT|IBAN|MEDICAL/i;
/**
 * Whether a DLP infoType is "sensitive" for alert tiering: curated metadata wins,
 * otherwise fall back to the name pattern. Non-matching types are not sensitive.
 */
export function isSensitivePiiInfoType(dlpName) {
    const curated = CURATED_BY_NAME.get(dlpName);
    if (curated)
        return curated.sensitivity === 'sensitive';
    return SENSITIVE_INFO_TYPE_PATTERN.test(dlpName);
}
/**
 * One selectable infoType returned by the picker endpoint (KIR-3837). Sourced
 * from Google DLP `infoTypes.list` and overlaid with Kirin's sensitivity /
 * higher-latency metadata so the picker matches the table.
 */
export const piiInfoTypeOptionSchema = z.object({
    /** DLP infoType name (e.g. EMAIL_ADDRESS). */
    name: z.string(),
    /** Google's human-readable display name, when available. */
    displayName: z.string().optional(),
    sensitivity: z.enum(['sensitive', 'not-sensitive']),
    higherLatency: z.boolean(),
    /** Illustrative value this detector catches (curated types only). */
    example: z.string().optional(),
});
export const piiInfoTypesListSchema = z.object({
    infoTypes: z.array(piiInfoTypeOptionSchema),
});
//# sourceMappingURL=pii-info-types.js.map